import { useEffect, useState } from 'react';
import type { Deck } from '@edh/shared';
import { fetchArt } from './api';
import { readArt, writeArt } from './db';

/**
 * Commander art, from the local blob store first and the network only if it is
 * missing. Spec 3b: the app has to show you your decks in a basement with no
 * wifi, so the bytes live on the device, not behind a CDN lookup.
 */

const objectUrls = new Map<string, string>();

/**
 * A partner's art is stored beside the first under its own key.
 *
 * Suffixed rather than given its own store: it is the same kind of thing, and
 * the cache, the warm-up and the offline story all work unchanged for free.
 */
const partnerKey = (deckId: string) => `${deckId}:partner`;

async function resolve(key: string, url: string | null): Promise<string | null> {
  const cachedUrl = objectUrls.get(key);
  if (cachedUrl) return cachedUrl;

  let blob = await readArt(key);

  if (!blob && url && navigator.onLine) {
    try {
      blob = await fetchArt(url);
      await writeArt(key, blob);
    } catch {
      return null; // offline or the proxy said no; the row renders without art
    }
  }
  if (!blob) return null;

  const objectUrl = URL.createObjectURL(blob);
  objectUrls.set(key, objectUrl);
  return objectUrl;
}

export function useDeckArt(deck: Deck | undefined): string | null {
  const [url, setUrl] = useState<string | null>(() =>
    deck ? (objectUrls.get(deck.id) ?? null) : null,
  );

  useEffect(() => {
    if (!deck) return;
    let cancelled = false;
    void resolve(deck.id, deck.artCropUrl).then((resolved) => {
      if (!cancelled) setUrl(resolved);
    });
    return () => {
      cancelled = true;
    };
    // Re-run if the deck gains art later (registered offline, filled in on sync).
  }, [deck?.id, deck?.artCropUrl]);

  /*
   * A custom deck's own picture wins, and needs none of the machinery above.
   *
   * It arrives with the league record as a data URI, so there is nothing to
   * fetch, nothing to cache and nothing to warm — it is already here and it
   * already works offline.
   */
  return deck?.customArt || url;
}

/** The second commander's art, or null for the decks that have one commander. */
export function usePartnerArt(deck: Deck | undefined): string | null {
  const [url, setUrl] = useState<string | null>(() =>
    deck?.partner ? (objectUrls.get(partnerKey(deck.id)) ?? null) : null,
  );

  useEffect(() => {
    if (!deck?.partner?.artCropUrl) {
      setUrl(null);
      return;
    }
    let cancelled = false;
    void resolve(partnerKey(deck.id), deck.partner.artCropUrl).then((resolved) => {
      if (!cancelled) setUrl(resolved);
    });
    return () => {
      cancelled = true;
    };
  }, [deck?.id, deck?.partner?.artCropUrl]);

  return url;
}

/** Pull art down for every deck that is missing it — called after a sync. */
export async function warmArtCache(decks: Deck[]): Promise<void> {
  for (const deck of decks) {
    for (const [key, url] of [
      [deck.id, deck.artCropUrl] as const,
      [partnerKey(deck.id), deck.partner?.artCropUrl ?? null] as const,
    ]) {
      if (!url) continue;
      if (await readArt(key)) continue;
      try {
        await writeArt(key, await fetchArt(url));
      } catch {
        return; // network is gone; whatever is left will be picked up next time
      }
    }
  }
}
